import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";
import { Loader2, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-Life tab — one grouped selector covering ALL life categories,
 * AI analysis via /astro-topic (topic=full-life), plus PDF export.
 */
const CATEGORIES = [
  { label: "Personality", items: [
    ["personality-analysis", "Personality analysis"],
    ["strengths-weaknesses", "Strengths and weaknesses"],
    ["life-purpose", "Life purpose (Dharma)"],
    ["hidden-talents", "Hidden talents"],
    ["character-development", "Character development"],
    ["spiritual-path", "Spiritual path"],
    ["destiny-vs-free-will", "Destiny vs free will"],
    ["lucky-habits", "Lucky habits to cultivate"],
  ]},
  { label: "Marriage & Relationships", items: [
    ["marriage-timing", "Marriage timing"],
    ["love-or-arranged", "Love or arranged marriage"],
    ["relationships-before-marriage", "Number of relationships before marriage"],
    ["spouse-personality", "Future spouse's personality"],
    ["spouse-profession", "Spouse's profession"],
    ["spouse-appearance", "Spouse's appearance"],
    ["spouse-family-background", "Spouse's family background"],
    ["age-difference-spouse", "Age difference with spouse"],
    ["compatibility-partner", "Compatibility with partner"],
    ["divorce-possibilities", "Divorce possibilities"],
    ["second-marriage", "Second marriage"],
    ["long-distance-marriage", "Long-distance marriage"],
    ["marriage-delays", "Delays in marriage"],
    ["married-life-happiness", "Married life happiness"],
    ["romance-intimacy", "Romance and intimacy"],
    ["extra-marital-risks", "Extra-marital risks"],
    ["relationship-remedies", "Relationship remedies"],
  ]},
  { label: "Children", items: [
    ["childbirth-timing", "Childbirth timing"],
    ["number-of-children", "Number of children"],
    ["gender-indications", "Gender indications (traditional; informational only)"],
    ["fertility-issues", "Fertility issues"],
    ["relationship-with-children", "Relationship with children"],
    ["children-education", "Children's education"],
    ["children-future", "Children's future"],
    ["adoption-prospects", "Adoption prospects"],
  ]},
  { label: "Career & Profession", items: [
    ["best-career-field", "Best career field"],
    ["government-or-private", "Government or private job"],
    ["business-or-employment", "Business or employment"],
    ["career-growth", "Career growth"],
    ["promotions", "Promotions"],
    ["job-change-timing", "Job change timing"],
    ["foreign-employment", "Foreign employment"],
    ["leadership-ability", "Leadership ability"],
    ["entrepreneurship", "Entrepreneurship"],
    ["politics", "Success in politics"],
    ["arts-media", "Acting, media, arts"],
    ["teaching", "Teaching profession"],
    ["medical", "Medical profession"],
    ["engineering", "Engineering"],
    ["law", "Law"],
    ["research", "Research"],
    ["freelancing", "Freelancing"],
    ["retirement-planning", "Retirement planning"],
  ]},
  { label: "Wealth & Finance", items: [
    ["financial-stability", "Financial stability"],
    ["wealth-accumulation", "Wealth accumulation"],
    ["sudden-gains", "Sudden gains"],
    ["lottery-speculation", "Lottery / speculation tendencies"],
    ["stock-market-suitability", "Stock market suitability"],
    ["real-estate-investments", "Real estate investments"],
    ["gold-investments", "Gold investments"],
    ["crypto-suitability", "Cryptocurrency suitability"],
    ["savings-habits", "Savings habits"],
    ["loans-debts", "Loans and debts"],
    ["financial-losses", "Financial losses"],
    ["passive-income", "Passive income"],
    ["multiple-income-sources", "Multiple income sources"],
    ["luxury-lifestyle", "Luxury lifestyle"],
    ["inheritance", "Inheritance"],
    ["family-wealth", "Family wealth"],
  ]},
  { label: "Business", items: [
    ["business-success", "Business success"],
    ["best-business-sectors", "Best business sectors"],
    ["partnership-business", "Partnership business"],
    ["business-expansion", "Business expansion"],
    ["startup-timing", "New startup timing"],
    ["business-risks", "Business risks"],
    ["competitor-analysis", "Competitor analysis"],
    ["cash-flow", "Cash flow"],
    ["international-business", "International business"],
    ["brand-success", "Brand success"],
  ]},
  { label: "Health", items: [
    ["general-health", "General health trends"],
    ["chronic-illness", "Chronic illness tendencies"],
    ["mental-health", "Mental health tendencies"],
    ["stress-anxiety", "Stress and anxiety periods"],
    ["surgery-possibilities", "Surgery possibilities"],
    ["accident-prone", "Accident-prone periods"],
    ["recovery-periods", "Recovery periods"],
    ["lifestyle-improvements", "Lifestyle improvements"],
    ["longevity", "Longevity"],
    ["preventive-health", "Preventive health focus"],
  ]},
  { label: "Education", items: [
    ["academic-success", "Academic success"],
    ["best-stream", "Best stream"],
    ["higher-education", "Higher education"],
    ["foreign-education", "Foreign education"],
    ["competitive-exams", "Competitive exams"],
    ["research-opportunities", "Research opportunities"],
    ["scholarships", "Scholarships"],
    ["learning-difficulties", "Learning difficulties"],
  ]},
  { label: "Foreign Travel & Settlement", items: [
    ["foreign-travel", "Foreign travel"],
    ["permanent-settlement-abroad", "Permanent settlement abroad"],
    ["visa-success", "Visa success"],
    ["immigration", "Immigration"],
    ["overseas-education", "Overseas education"],
    ["overseas-employment", "Overseas employment"],
    ["pilgrimages", "Pilgrimages"],
    ["frequent-travel", "Frequent travel"],
  ]},
  { label: "Property & Assets", items: [
    ["buying-house", "Buying a house"],
    ["selling-property", "Selling property"],
    ["land-investment", "Land investment"],
    ["vehicle-purchase", "Vehicle purchase"],
    ["farm-land", "Farm land"],
    ["rental-income", "Rental income"],
    ["home-construction", "Home construction"],
    ["vastu-considerations", "Vastu considerations"],
  ]},
  { label: "Family", items: [
    ["parents", "Relationship with parents"],
    ["siblings", "Siblings"],
    ["family-harmony", "Family harmony"],
    ["family-disputes", "Family disputes"],
    ["family-inheritance", "Inheritance"],
    ["family-business", "Family business"],
    ["ancestral-property", "Ancestral property"],
    ["care-of-elders", "Care of elders"],
  ]},
  { label: "Legal Matters", items: [
    ["court-cases", "Court cases"],
    ["litigation-outcome", "Litigation outcome"],
    ["property-disputes", "Property disputes"],
    ["government-issues", "Government issues"],
    ["tax-matters", "Tax matters"],
    ["police-matters", "Police matters"],
    ["settlements", "Settlements"],
  ]},
  { label: "Spiritual Growth", items: [
    ["ishta-devata", "Ishta Devata"],
    ["meditation-suitability", "Meditation suitability"],
    ["mantras", "Mantras"],
    ["temple-worship", "Temple worship"],
    ["spiritual-pilgrimages", "Pilgrimages"],
    ["guru-guidance", "Guru guidance"],
    ["moksha-indications", "Moksha indications"],
    ["spiritual-awakening", "Spiritual awakening"],
  ]},
  { label: "Fame & Recognition", items: [
    ["public-recognition", "Public recognition"],
    ["social-influence", "Social influence"],
    ["political-success", "Political success"],
    ["awards", "Awards"],
    ["celebrity-potential", "Celebrity potential"],
    ["social-media-growth", "Social media growth"],
    ["writing-publishing", "Writing and publishing"],
  ]},
  { label: "Timing (Prediction)", items: [
    ["good-years", "Good years"],
    ["challenging-years", "Challenging years"],
  ]},
];

export default function FullLifePanel({ profileId }) {
  const flatMap = useMemo(() => {
    const m = {};
    CATEGORIES.forEach(g => g.items.forEach(([k, l]) => { m[k] = { label: l, category: g.label }; }));
    return m;
  }, []);
  const [subtopic, setSubtopic] = useState(CATEGORIES[0].items[0][0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try { setData(await api.astroTopic(profileId, "full-life", subtopic)); }
    catch (err) {
      console.error("full-life topic failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [profileId, subtopic]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    if (!data?.content) { toast.error("Generate the analysis first."); return; }
    setDownloading(true);
    try {
      const meta = flatMap[subtopic] || { label: subtopic, category: "Full Life" };
      const resp = await fetch(api.astroTopicPdfUrl(profileId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: "full-life", subtopic,
          topic_label: `Full Life — ${meta.category}`,
          subtopic_label: meta.label,
          lang: (localStorage.getItem("jyotish-lang") || "en"),
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FullLife_${meta.category.replace(/\s+/g, "_")}_${meta.label.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("PDF download failed");
    }
    setDownloading(false);
  };

  const renderInline = (text) => {
    const parts = []; let rest = text; let idx = 0;
    while (rest.length) {
      const b = rest.indexOf("**");
      if (b < 0) { parts.push(rest); break; }
      if (b > 0) parts.push(rest.slice(0, b));
      const end = rest.indexOf("**", b + 2);
      if (end < 0) { parts.push(rest.slice(b)); break; }
      parts.push(<strong key={`b${idx++}`} className="text-[#F0E7C8]">{rest.slice(b + 2, end)}</strong>);
      rest = rest.slice(end + 2);
    }
    return parts;
  };

  const render = (txt) => {
    if (!txt) return null;
    const lines = txt.split("\n");
    const out = [];
    let list = null;
    const flushList = () => {
      if (!list) return;
      const Tag = list.type === "ol" ? "ol" : "ul";
      out.push(
        <Tag key={`list-${out.length}`}
             className={`${list.type === "ol" ? "list-decimal" : "list-disc"} ml-6 space-y-1 my-2`}>
          {list.items.map((it, idx) => (
            <li key={`li-${idx}`} className="text-sm text-[#E6E1D6] leading-relaxed">{renderInline(it)}</li>
          ))}
        </Tag>,
      );
      list = null;
    };
    lines.forEach((l, i) => {
      const k = `L${i}-${l.slice(0, 24)}`;
      const t = l.trim();
      const isBullet = l.startsWith("- ") || l.startsWith("• ") || l.startsWith("* ");
      const m = l.match(/^(\d+)[.)]\s+(.*)$/);
      if (isBullet) {
        if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
        list.items.push(l.slice(2)); return;
      }
      if (m) {
        if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
        list.items.push(m[2]); return;
      }
      flushList();
      if (t === "---" || t === "***" || t === "___") { out.push(<hr key={k} className="my-3 border-[#2A2D35]" />); return; }
      if (l.startsWith("### ")) { out.push(<h4 key={k} className="font-heading text-base text-[#A8896C] mt-3 mb-1">{renderInline(l.slice(4))}</h4>); return; }
      if (l.startsWith("## ")) { out.push(<h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-4 mb-2">{renderInline(l.slice(3))}</h3>); return; }
      if (l.startsWith("# ")) { out.push(<h2 key={k} className="font-heading text-xl text-[#E6E1D6] mt-4 mb-2">{renderInline(l.slice(2))}</h2>); return; }
      if (!l.trim()) { out.push(<div key={k} className="h-2" />); return; }
      out.push(<p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light">{renderInline(l)}</p>);
    });
    flushList();
    return out;
  };

  const currentLabel = flatMap[subtopic]?.label || subtopic;
  const currentCat = flatMap[subtopic]?.category || "";

  return (
    <div className="space-y-5" data-testid="topic-panel-full-life">
      <div className="border-l-4 border-[#D4AF37] pl-3">
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Full Life Analysis</div>
        <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
          Complete Vedic analysis — 15 life areas, 140+ specific questions. Pick a focus, generate, and export a PDF.
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono mb-2">
            Category · Focus {currentCat && <span className="text-[#D4AF37]">— {currentCat}</span>}
          </div>
          <Select value={subtopic} onValueChange={setSubtopic}>
            <SelectTrigger data-testid="fulllife-subtopic-select"
              className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-[420px]">
              {CATEGORIES.map((g, gi) => (
                <SelectGroup key={g.label}>
                  {gi > 0 && <SelectSeparator className="bg-[#2A2D35]" />}
                  <SelectLabel className="text-[#D4AF37] font-mono uppercase text-[10px] tracking-widest">
                    {g.label}
                  </SelectLabel>
                  {g.items.map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button data-testid="fulllife-generate" onClick={load} disabled={loading}
          className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Reading…</>
                    : <><Sparkles size={14} className="mr-2" /> Generate</>}
        </Button>
        <Button data-testid="fulllife-download-pdf" onClick={downloadPdf}
          disabled={!data?.content || downloading}
          className="rounded-sm border border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10">
          {downloading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Preparing…</>
                        : <><Download size={14} className="mr-2" /> PDF</>}
        </Button>
      </div>

      {loading && (
        <div className="text-[#A19E95] font-mono text-sm">
          Consulting your chart on <span className="text-[#D4AF37]">{currentLabel}</span>… this can take 20-60 seconds on first request.
        </div>
      )}

      {data && !loading && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-5 space-y-1"
             data-testid="topic-full-life-content">
          {render(data.content)}
        </div>
      )}
    </div>
  );
}
